import * as _ from "lodash-es";
import React, { useContext, useState, useEffect, useCallback } from "react";
import IAttachment from "../../../models/IAttachment";
import ICatalog_Item from "../../../models/ICatalog_Item";
import IImageSizeEnum from "../../../models/IImageSizeEnum";
import "./OwS3ImageViewer.scss";
import styled from "styled-components";
import { Placeholder } from "react-bootstrap";
import useEmblaCarousel from "embla-carousel-react";
import UiContext from "../../../contexts/UiContext";
import { EmblaOptionsType } from "embla-carousel";
import { OwCarousel } from "../OwCarousel/OwCarousel";

export interface Props {
    catalogItem?: ICatalog_Item;
    attachmentIndex?: number;
    imageIndex?: IImageSizeEnum;
    modalIndex?: IImageSizeEnum;
    isMicroView?: boolean;
    className?: string;
    includeAllImages?: boolean; // Should we include all the images in the modal window for scrolling
    hideEmptyImage?: boolean;
    imageSeq?: number;
    totalSeq?: number; // We use this to determine the method of finding the correct attachment to handle
    microViewSize?: "5rem" | "10rem" | "15rem";
    allowMicroViewScroll?: boolean;
}

const defaultProps: Partial<Props> = {
    isMicroView: false,
    imageIndex: 0, // thumbnail
    modalIndex: 3, // medium
};

const OwS3ImageViewer = (props: Props) => {
    props = { ...defaultProps, ...props };
    const uiContext = useContext(UiContext);

    // open the image carousel
    const openModal = () => {
        console.log("openModal", "setModalSetup");

        // IMPORTANT FIX: Always collect all images for the carousel
        // regardless of the includeAllImages setting
        const carouselImages = [];

        // If we have a catalog item, get all images for the carousel
        if (props.catalogItem && props.catalogItem.Attachments) {
            console.log("Getting all images for carousel");

            const imageSeqAttachments = _.reject(props.catalogItem.Attachments, ["image_seq", null]);
            const attachments = _.sortBy(imageSeqAttachments, "image_seq");

            console.log("Found attachments:", attachments.length);

            for (const attachment of attachments) {
                if (attachment.processedDetail && attachment.processedDetail[0]) {
                    const { signedUrls } = attachment.processedDetail[0];
                    // Use LARGE size for modal view
                    const modalIndex = props.modalIndex || IImageSizeEnum.LARGE;
                    const modalSrc = signedUrls[modalIndex];
                    if (modalSrc) {
                        console.log("Adding image to carousel:", modalSrc);
                        carouselImages.push({ src: modalSrc });
                    }
                }
            }

            console.log("Total carousel images:", carouselImages.length);
        }

        // If we don't have any images collected, use the current image
        if (carouselImages.length === 0 && images.length > 0) {
            console.log("No attachments found, using current image");
            carouselImages.push(...images);
        }

        console.log("carouselImages: ", carouselImages);

        uiContext.setModalSetup({ modalContentClass: "modalImageCarousel", modalOverlayClass: "modalImageOverlay" });
        uiContext.setModalComponent(<OwCarousel carouselImages={carouselImages} />);

        uiContext.showModal(true);
    };

    const getAttachment = (): IAttachment | null => {
        if (props.catalogItem) {
            const imageSeqAttachments = _.reject(props.catalogItem.Attachments, ["image_seq", null]);
            const attachments = _.sortBy(imageSeqAttachments, "image_seq");

            // TODO - need the total available attachments for the sequence, if the current attachments is less than the length, then we need to search it for the image sequence.
            if (props.totalSeq && props.totalSeq > attachments.length) {
                let attachment: any = null;

                for (const _attachment of attachments) {
                    if (_attachment.image_seq === props.imageSeq) {
                        attachment = _attachment;
                        break;
                    }
                }

                return attachment;
            } else {
                const attachment = attachments[props.attachmentIndex || 0];

                return attachment;
            }
        } else {
            return null;
        }
    };

    if (!props.catalogItem || !props.catalogItem.Attachments) {
        return null;
    }

    const attachment = getAttachment();

    if (props.imageSeq) {
        if (attachment && attachment.image_seq !== props.imageSeq) {
            return (
                <div
                    className={
                        " OwS3ImageViewer " + (props.isMicroView ? "microView" : "") + " " + (props.className ? props.className : "")
                    }
                />
            );
        }
    }

    let image: any = null;
    const images: any[] = [];

    let currentIndex = 0;

    if (!props.isMicroView && props.attachmentIndex) {
        currentIndex = props.attachmentIndex || 0;
    }

    let hasImageUrl = false;
    let src = "";

    if (attachment && attachment.processedDetail) {
        const processedDetail = attachment.processedDetail[0];

        const { signedUrls } = processedDetail;
        // 0 - Thumb
        // 1 - Small
        // 2 - Medium
        // 3 - Large
        // 4 - XLarge

        if (props.isMicroView && signedUrls[0]) {
            hasImageUrl = true;
            src = signedUrls[0];
            const modalSrc = signedUrls[IImageSizeEnum.LARGE];

            // For microView, we still need at least the current image for the thumbnail
            if (!props.includeAllImages) {
                images.push({ src: modalSrc });
            }

            if (src.length) {
                image = (
                    <img
                        className="image"
                        src={src}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();

                            openModal();
                        }}
                    />
                );
            }
        } else {
            const imageIndex = props.imageIndex || IImageSizeEnum.THUMB;
            const modalIndex = props.modalIndex || IImageSizeEnum.LARGE;

            if (signedUrls[imageIndex]) {
                src = signedUrls[imageIndex];
                const modalSrc = signedUrls[modalIndex];

                if (!props.includeAllImages) {
                    images.push({ src: modalSrc });
                }

                if (src.length) {
                    image = (
                        <img
                            src={src}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();

                                openModal();
                            }}
                        />
                    );
                }
            }
        }
    }

    if (props.includeAllImages && props.catalogItem && props.catalogItem.Attachments) {
        const imageSeqAttachments = _.reject(props.catalogItem.Attachments, ["image_seq", null]);
        const attachments = _.sortBy(imageSeqAttachments, "image_seq");

        for (const attachment of attachments) {
            if (attachment.processedDetail && attachment.processedDetail[0]) {
                const { signedUrls } = attachment.processedDetail[0];
                const modalSrc = signedUrls[IImageSizeEnum.LARGE];
                images.push({ src: modalSrc });
            }
        }
    }

    let hasImage = false;

    if (image) {
        hasImage = true;
    }

    let wrapperClassName = "OwS3ImageViewer";

    if (image) {
        wrapperClassName += " pointer";
    } else {
        if (props.hideEmptyImage) {
            wrapperClassName += " d-none";
        }
    }

    if (props.isMicroView) {
        wrapperClassName += " microView";
    }

    if (props.className) {
        wrapperClassName += ` ${props.className}`;
    }

    if (hasImage) {
        wrapperClassName += " hasImage";
    }

    return <div className={wrapperClassName}>{hasImage ? image : <div className="imagePlaceholder" />}</div>;
};

export default OwS3ImageViewer;

const ImageViewerDiv = styled.div``;

const Img = styled.img``;
